import { useAuth } from "@/contexts/AuthContext";
import { useAPI } from "@/hooks/useAPI";
import { useCanReadSubscriptions, usePermissionsV2Loaded } from "@/permissions/v2";
import { CreditBalanceService } from "@/services/creditBalanceService";
import type { CreditBalanceSummaryResponse } from "@/types/creditBalance";
import { mutate } from "swr";
import useSWR from "swr";

const CREDIT_BALANCE_KEY = "/credit-balance";

export const useCreditBalance = () => {
  const api = useAPI("v2");
  const { firmContext } = useAuth();
  const creditBalanceService = new CreditBalanceService(api);

  const isPermissionsLoaded = usePermissionsV2Loaded();
  const canViewCreditBalance = useCanReadSubscriptions(firmContext?.firmId);
  const cacheKey =
    isPermissionsLoaded && canViewCreditBalance && firmContext?.firmId
      ? [CREDIT_BALANCE_KEY, firmContext.firmId]
      : null;

  const { data, error, isLoading } = useSWR<CreditBalanceSummaryResponse>(
    cacheKey,
    () => {
      if (!firmContext?.firmId) {
        throw new Error("Firm context is not available");
      }
      return creditBalanceService.getCreditBalance(firmContext.firmId);
    },
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 300000, // 5 minutes
    }
  );

  return {
    creditBalance: data || null,
    isLoading,
    isError: error,
  };
};

/**
 * Invalidates the credit balance cache.
 * Call this after a subscription purchase to refresh the credit balance.
 */
export const invalidateCreditBalanceCache = () => {
  mutate((key: unknown) => Array.isArray(key) && key[0] === CREDIT_BALANCE_KEY, undefined, {
    revalidate: true,
  });
};
